import type {
  Stripe,
  StripeCardElement,
  StripeCardElementChangeEvent,
  StripeElements,
  StripePaymentElement,
} from "@stripe/stripe-js";
import type { EmbedStripe } from "../config/types";
import { loadEmbedStripe } from "./stripe-client";

export interface StripeCardStatus {
  ready: boolean;
  complete: boolean;
  error: string | null;
}

export interface StripeBillingDetails {
  name: string;
  email: string;
  phone?: string;
}

export type StripeConfirmationMode = "setup" | "payment";

export interface StripeAdapterOptions {
  load?: (config: EmbedStripe) => Promise<Stripe | null>;
  onStatus?: (status: StripeCardStatus) => void;
}

/**
 * The only object allowed to retain Stripe.js and DOM mount references for the
 * commerce actor path. State machines exchange opaque payment-method tokens;
 * they never retain vendor objects, callbacks, or nodes in serializable state.
 */
export class StripeDomPaymentAdapter {
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private card: StripeCardElement | null = null;
  private payment: StripePaymentElement | null = null;
  private mountTarget: HTMLElement | null = null;
  private lifecycle = 0;
  private status: StripeCardStatus = {
    ready: false,
    complete: false,
    error: null,
  };

  private readonly load: (config: EmbedStripe) => Promise<Stripe | null>;
  private readonly onStatus: (status: StripeCardStatus) => void;

  constructor(options: StripeAdapterOptions = {}) {
    this.load = options.load ?? loadEmbedStripe;
    this.onStatus = options.onStatus ?? (() => undefined);
  }

  async initialize(config: EmbedStripe): Promise<void> {
    this.teardown();
    this.stripe = await this.load(config);
    if (!this.stripe) throw new Error("Stripe failed to load");
    this.elements = this.stripe.elements();
  }

  mountCard(target: HTMLElement): void {
    if (!this.elements) {
      throw new Error("Stripe must be initialized before mounting a card");
    }
    this.destroyCard();
    const card = this.elements.create("card");
    const lifecycle = ++this.lifecycle;
    card.on("ready", () => {
      if (lifecycle !== this.lifecycle) return;
      this.updateStatus({ ready: true });
    });
    card.on("change", (event: StripeCardElementChangeEvent) => {
      if (lifecycle !== this.lifecycle) return;
      this.updateStatus({
        complete: event.complete,
        error: event.error?.message ?? null,
      });
    });
    card.mount(target);
    this.card = card;
    this.mountTarget = target;
  }

  mountPaymentElement(target: HTMLElement, clientSecret: string): void {
    if (!this.stripe) {
      throw new Error("Stripe must be initialized before mounting payment");
    }
    this.destroyCard();
    this.destroyPayment();
    this.elements = this.stripe.elements({ clientSecret });
    const payment = this.elements.create("payment", {
      layout: "tabs",
    });
    const lifecycle = ++this.lifecycle;
    payment.on("ready", () => {
      if (lifecycle !== this.lifecycle) return;
      this.updateStatus({ ready: true });
    });
    payment.on("change", (event) => {
      if (lifecycle !== this.lifecycle) return;
      this.updateStatus({
        complete: event.complete,
        error: event.value.type ? null : this.status.error,
      });
    });
    payment.mount(target);
    this.payment = payment;
    this.mountTarget = target;
  }

  async confirmAction(input: {
    clientSecret: string;
    returnUrl: string;
    mode: StripeConfirmationMode;
    billingDetails?: StripeBillingDetails;
  }): Promise<string | null> {
    if (!this.stripe || !this.elements || !this.payment) {
      throw new Error("The payment field is not mounted");
    }
    if (!this.mountTarget?.isConnected) {
      throw new Error("The payment field is detached");
    }
    const confirmParams = {
      return_url: input.returnUrl,
      ...(input.billingDetails
        ? {
            payment_method_data: {
              billing_details: {
                name: input.billingDetails.name,
                email: input.billingDetails.email,
                ...(input.billingDetails.phone
                  ? { phone: input.billingDetails.phone }
                  : {}),
              },
            },
          }
        : {}),
    };
    if (input.mode === "payment") {
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: input.clientSecret,
        confirmParams,
        redirect: "if_required",
      });
      if (result.error) {
        throw new Error(result.error.message ?? "Unable to confirm payment");
      }
      const paymentMethod = result.paymentIntent.payment_method;
      if (!paymentMethod) return null;
      return typeof paymentMethod === "string"
        ? paymentMethod
        : paymentMethod.id;
    }
    const result = await this.stripe.confirmSetup({
      elements: this.elements,
      clientSecret: input.clientSecret,
      confirmParams,
      redirect: "if_required",
    });
    if (result.error) {
      throw new Error(result.error.message ?? "Unable to confirm payment");
    }
    // A bank account (ACH) that Financial Connections couldn't verify
    // instantly stays `requires_action` until micro-deposits land. It has a
    // PaymentMethod already, but nothing may be charged against it yet.
    if (result.setupIntent.status === "requires_action") {
      throw new Error(
        "Your bank account requires additional verification. Please check " +
          "your email for micro-deposit verification instructions.",
      );
    }
    const paymentMethod = result.setupIntent.payment_method;
    if (!paymentMethod) {
      return null;
    }
    return typeof paymentMethod === "string" ? paymentMethod : paymentMethod.id;
  }

  async confirmSetup(
    input: Omit<
      Parameters<StripeDomPaymentAdapter["confirmAction"]>[0],
      "mode"
    >,
  ): Promise<string | null> {
    return this.confirmAction({ ...input, mode: "setup" });
  }

  async collectPaymentMethod(
    billingDetails: StripeBillingDetails,
  ): Promise<string> {
    if (!this.stripe || !this.card || !this.mountTarget?.isConnected) {
      throw new Error("The card field is not mounted");
    }
    if (!this.status.ready || !this.status.complete) {
      throw new Error(this.status.error ?? "Card details are incomplete");
    }
    const result = await this.stripe.createPaymentMethod({
      type: "card",
      card: this.card,
      billing_details: {
        name: billingDetails.name,
        email: billingDetails.email,
        ...(billingDetails.phone ? { phone: billingDetails.phone } : {}),
      },
    });
    if (result.error) {
      throw new Error(result.error.message ?? "Unable to tokenize card");
    }
    return result.paymentMethod.id;
  }

  teardown(): void {
    this.destroyCard();
    this.destroyPayment();
    this.elements = null;
    this.stripe = null;
  }

  private destroyCard(): void {
    this.lifecycle += 1;
    if (this.card) {
      this.card.unmount();
      this.card.destroy();
    }
    this.card = null;
    this.mountTarget = null;
    this.status = { ready: false, complete: false, error: null };
    this.onStatus(this.status);
  }

  private destroyPayment(): void {
    this.lifecycle += 1;
    if (this.payment) {
      this.payment.unmount();
      this.payment.destroy();
    }
    this.payment = null;
    this.mountTarget = null;
  }

  private updateStatus(change: Partial<StripeCardStatus>): void {
    this.status = { ...this.status, ...change };
    this.onStatus(this.status);
  }
}
